/**
 * Opt-in "keep my entries on this device" for planner and tracker tools.
 *
 * Privacy contract (keep in step with the in-UI wording):
 *  - OFF by default. Nothing is written until the person turns it on for that tool.
 *  - Data goes to this browser's localStorage only. It is never sent to our servers.
 *  - Turning it off, or "Delete everything saved here", removes the saved copy.
 *  - Every failure (storage full, blocked, private mode, corrupted copy) is reported, never thrown.
 *
 * The functions take the storage as an argument so they can be tested without a browser. Any tool can use
 * them: pick a stable `toolId`, decide what goes in the snapshot (leave out files and anything large), and
 * render <DeviceKeepPanel>. See useDeviceKeep for the React wiring.
 */
export type KeepStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export type KeepFailure = "full" | "blocked" | "corrupt" | "too_big";

export interface KeepRecord<T> { v: 1; savedAt: string; data: T }

export const KEEP_PREFIX = "fdp-keep:v1:";
/** Anything bigger than this is refused so one tool cannot fill the browser's quota for the whole site. */
export const MAX_KEEP_BYTES = 3_000_000;

const dataKey = (toolId: string) => `${KEEP_PREFIX}${toolId}:data`;
const flagKey = (toolId: string) => `${KEEP_PREFIX}${toolId}:on`;

export const keepMessage: Record<KeepFailure, string> = {
  full: "This browser has no room left to save your entries here. They are still on screen: use Save project or export a file to keep them.",
  blocked: "This browser is not letting this page save on the device (private browsing or blocked site data). Your entries stay on screen for this visit only: use Save project or export a file to keep them.",
  corrupt: "The copy saved on this device could not be read, so it was cleared. Open a project file or backup if you have one.",
  too_big: "Your entries are too large to keep on this device. Use Save project or export a file to keep them.",
};

/** The browser's localStorage, or null when it cannot be touched at all. */
export function browserStorage(): KeepStorage | null {
  try { return typeof localStorage !== "undefined" ? localStorage : null; } catch { return null; }
}

const isQuota = (e: unknown): boolean => {
  const x = e as { name?: string; code?: number } | null;
  return !!x && (x.name === "QuotaExceededError" || x.name === "NS_ERROR_DOM_QUOTA_REACHED" || x.code === 22 || x.code === 1014);
};

export function isKeepOn(storage: KeepStorage | null, toolId: string): boolean {
  if (!storage) return false;
  try { return storage.getItem(flagKey(toolId)) === "1"; } catch { return false; }
}

export type SetOnResult = { ok: true } | { ok: false; error: KeepFailure };

/** Turn keeping on or off. Turning it off also deletes the saved copy. */
export function setKeepOn(storage: KeepStorage | null, toolId: string, on: boolean): SetOnResult {
  if (!storage) return on ? { ok: false, error: "blocked" } : { ok: true };
  try {
    if (on) storage.setItem(flagKey(toolId), "1");
    else { storage.removeItem(flagKey(toolId)); storage.removeItem(dataKey(toolId)); }
    return { ok: true };
  } catch (e) {
    return on ? { ok: false, error: isQuota(e) ? "full" : "blocked" } : { ok: true };
  }
}

export type LoadResult<T> =
  | { status: "none" }
  | { status: "ok"; data: T; savedAt: string }
  | { status: "error"; error: KeepFailure };

/** Read the saved copy. A damaged copy is removed so it cannot fail again. `validate` rejects the wrong shape. */
export function loadKept<T>(storage: KeepStorage | null, toolId: string, validate?: (raw: unknown) => T | null): LoadResult<T> {
  if (!storage) return { status: "none" };
  let raw: string | null;
  try { raw = storage.getItem(dataKey(toolId)); } catch { return { status: "error", error: "blocked" }; }
  if (!raw) return { status: "none" };
  try {
    const rec = JSON.parse(raw) as Partial<KeepRecord<unknown>> | null;
    if (!rec || rec.v !== 1 || typeof rec.savedAt !== "string" || !("data" in rec)) throw new Error("shape");
    const data = validate ? validate(rec.data) : (rec.data as T);
    if (data === null || data === undefined) throw new Error("invalid");
    return { status: "ok", data, savedAt: rec.savedAt };
  } catch {
    try { storage.removeItem(dataKey(toolId)); } catch { /* ignore */ }
    return { status: "error", error: "corrupt" };
  }
}

export type SaveResult = { ok: true; savedAt: string } | { ok: false; error: KeepFailure };

/** Write the snapshot. Does nothing unless the person turned keeping on for this tool. */
export function saveKept<T>(storage: KeepStorage | null, toolId: string, data: T, now: Date = new Date()): SaveResult {
  if (!isKeepOn(storage, toolId)) return { ok: false, error: "blocked" };
  const savedAt = now.toISOString();
  let text: string;
  try { text = JSON.stringify({ v: 1, savedAt, data } satisfies KeepRecord<T>); } catch { return { ok: false, error: "too_big" }; }
  if (text.length > MAX_KEEP_BYTES) return { ok: false, error: "too_big" };
  try { storage!.setItem(dataKey(toolId), text); return { ok: true, savedAt }; } catch (e) { return { ok: false, error: isQuota(e) ? "full" : "blocked" }; }
}

/** Delete the saved copy (keeps the on/off choice). */
export function wipeKept(storage: KeepStorage | null, toolId: string): boolean {
  if (!storage) return true;
  try { storage.removeItem(dataKey(toolId)); return true; } catch { return false; }
}

/** Delete the saved copy and switch keeping off. */
export function forgetEverything(storage: KeepStorage | null, toolId: string): boolean {
  if (!storage) return true;
  try { storage.removeItem(dataKey(toolId)); storage.removeItem(flagKey(toolId)); return true; } catch { return false; }
}
